import { emptySeriesFilters } from "../../data/product";
import {
	ProductDataRow,
	SeriesFilterKey,
	SeriesFilters,
	SeriesViewState,
} from "../../types/types";

export function normalizeSeriesKey(value: string): string {
	return value.trim().toLowerCase();
}

/** HTML id fragment: trim and drop spaces so ids stay valid. */
export function normalizeSeriesLabel(value: string): string {
	return value.trim().replace(/\s+/g, "");
}

/** Re-keys an API map so lookups can use a normalized series code. */
export function normalizeSeriesMap(
	map: Record<string, string>,
): Record<string, string> {
	const normalized: Record<string, string> = {};

	for (const [code, value] of Object.entries(map)) {
		normalized[normalizeSeriesKey(code)] = value;
	}

	return normalized;
}

/**
 * Series codes to send to the product-data API.
 */
export function seriesLookupCodes(seriesCodes: string[]): string[] {
	const lookup = new Set<string>();

	for (const code of seriesCodes) {
		const normalized = normalizeSeriesKey(code);
		if (!normalized) {
			continue;
		}

		lookup.add(normalized);
	}

	return Array.from(lookup);
}

export function productsForSeries(
	products: ProductDataRow[],
	seriesCode: string,
): ProductDataRow[] {
	const target = normalizeSeriesKey(seriesCode);
	return products.filter(
		(product) => normalizeSeriesKey(product.series) === target,
	);
}

export function uniqueJoinedValues(
	products: ProductDataRow[],
	key: keyof ProductDataRow,
	separator = " / ",
	formatter?: (value: string) => string,
): string {
	const values = Array.from(
		new Set(
			products
				.map((product) => product[key].trim())
				.filter((value) => value !== ""),
		),
	);
	return values.map((v) => (formatter ? formatter(v) : v)).join(separator);
}

export function uniqueFilterOptions(
	products: ProductDataRow[],
	key: SeriesFilterKey,
): string[] {
	return Array.from(
		new Set(
			products
				.map((product) => product[key].trim())
				.filter((value) => value !== ""),
		),
	).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

/** Display units for filter labels and table cells (raw DB values stay unformatted). */
export function formatProductFieldValue(
	key: SeriesFilterKey,
	value: string,
): string {
	const trimmed = value.trim();
	if (trimmed === "") {
		return "";
	}

	switch (key) {
		case "cordLengthInFeet":
			return /'$/.test(trimmed) ? trimmed : `${trimmed}'`;
		case "solidsHandlingInInches":
		case "dischargeSizeInInches":
			return /"$/.test(trimmed) ? trimmed : `${trimmed}"`;
		default:
			return trimmed;
	}
}

/** Drops blank filter values so saved submissions stay compact. */
export function toSeriesViewState(
	hiddenSeries: ReadonlySet<string>,
	filtersBySeries: Record<string, SeriesFilters>,
): SeriesViewState {
	const filters: Record<string, Partial<SeriesFilters>> = {};

	for (const [seriesCode, seriesFilters] of Object.entries(filtersBySeries)) {
		const active: Partial<SeriesFilters> = {};

		for (const key of Object.keys(seriesFilters) as SeriesFilterKey[]) {
			const value = seriesFilters[key].trim();
			if (value !== "") {
				active[key] = value;
			}
		}

		if (Object.keys(active).length > 0) {
			filters[seriesCode] = active;
		}
	}

	return {
		hiddenSeries: Array.from(hiddenSeries),
		filters,
	};
}

/** Rebuilds runtime state from a saved submission, tolerating a missing key. */
export function fromSeriesViewState(seriesView?: SeriesViewState): {
	hiddenSeries: Set<string>;
	filtersBySeries: Record<string, SeriesFilters>;
} {
	const filtersBySeries: Record<string, SeriesFilters> = {};

	for (const [seriesCode, saved] of Object.entries(seriesView?.filters ?? {})) {
		filtersBySeries[seriesCode] = { ...emptySeriesFilters(), ...saved };
	}

	return {
		hiddenSeries: new Set(seriesView?.hiddenSeries ?? []),
		filtersBySeries,
	};
}

export function filterProducts(
	products: ProductDataRow[],
	filters: SeriesFilters,
): ProductDataRow[] {
	return products.filter((product) =>
		(Object.keys(filters) as SeriesFilterKey[]).every((key) => {
			const selected = filters[key];
			if (!selected) {
				return true;
			}
			return product[key].trim() === selected;
		}),
	);
}
